import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import * as CANNON from "cannon-es";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import styles from "./LayeredDataCore.module.css";

const NUM_ELEMENTS = 6;
const CYLINDER_LENGTH = 2.6;
const PORT_OFFSET = CYLINDER_LENGTH / 2;
const SNAP_DISTANCE = 0.5;
const MAGNET_DISTANCE = 4.0;
const BASE_ATTRACTION_STRENGTH = 7.2;
const BASE_REPULSION_STRENGTH = -4.2;
const ATTRACTION_RAMP_PER_SECOND = 8.5;
const MAX_ATTRACTION_STRENGTH = 26;
const COLORS = [0xf2f4f8, 0x2f6bff, 0x151821];
const LOCAL_PORTS = [
  new CANNON.Vec3(PORT_OFFSET, 0, 0),
  new CANNON.Vec3(-PORT_OFFSET, 0, 0),
  new CANNON.Vec3(0, PORT_OFFSET, 0),
  new CANNON.Vec3(0, -PORT_OFFSET, 0),
  new CANNON.Vec3(0, 0, PORT_OFFSET),
  new CANNON.Vec3(0, 0, -PORT_OFFSET),
];
const DEBUG_LINE_COUNT = NUM_ELEMENTS * LOCAL_PORTS.length * 2;

function createConnectorMesh(geometry, material) {
  const group = new THREE.Group();

  for (let i = 0; i < 3; i += 1) {
    const mesh = new THREE.Mesh(geometry, material);

    if (i === 1) mesh.rotation.z = Math.PI / 2;
    if (i === 2) mesh.rotation.x = Math.PI / 2;

    group.add(mesh);
  }

  return group;
}

function createBody() {
  const body = new CANNON.Body({
    mass: 1,
    linearDamping: 0.54,
    angularDamping: 0.86,
  });
  const halfExtents = new CANNON.Vec3(0.35, CYLINDER_LENGTH / 2, 0.35);
  const rotationZ = new CANNON.Quaternion();
  const rotationX = new CANNON.Quaternion();

  rotationZ.setFromEuler(0, 0, Math.PI / 2);
  rotationX.setFromEuler(Math.PI / 2, 0, 0);

  body.addShape(new CANNON.Box(halfExtents));
  body.addShape(new CANNON.Box(halfExtents), new CANNON.Vec3(), rotationZ);
  body.addShape(new CANNON.Box(halfExtents), new CANNON.Vec3(), rotationX);

  body.position.set(
    (Math.random() - 0.5) * 14 + 2.4,
    (Math.random() - 0.5) * 12 + 1.8,
    (Math.random() - 0.5) * 12
  );
  body.velocity.set(
    (Math.random() - 0.5) * 0.45,
    (Math.random() - 0.5) * 0.45,
    (Math.random() - 0.5) * 0.45
  );
  body.angularVelocity.set(
    Math.random() * 0.08,
    Math.random() * 0.08,
    Math.random() * 0.08
  );

  return body;
}

function LayeredDataCore() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 1000);
    camera.position.set(13, 10, 18);

    const renderer = new THREE.WebGLRenderer({
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);
    renderer.domElement.classList.add(styles.canvas);
    let hasValidViewport = false;
    let isSceneVisible = false;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.08;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.rotateSpeed = 0.14;
    controls.minPolarAngle = 0.78;
    controls.maxPolarAngle = 1.08;
    controls.target.set(0.5, 0.4, 0);

    const world = new CANNON.World();
    world.gravity.set(0, 0, 0);
    world.solver.iterations = 50;

    const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    scene.add(ambientLight);

    const hemisphereLight = new THREE.HemisphereLight(0xa9c3ff, 0x040608, 1.45);
    scene.add(hemisphereLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
    keyLight.position.set(14, 12, 10);
    scene.add(keyLight);

    const rimLight = new THREE.PointLight(0x4da3ff, 24, 60, 2);
    rimLight.position.set(-10, 4, 12);
    scene.add(rimLight);

    const fillLight = new THREE.PointLight(0x7f8cff, 12, 50, 2);
    fillLight.position.set(6, -8, 8);
    scene.add(fillLight);

    const sharedGeometry = new THREE.CylinderGeometry(
      0.35,
      0.35,
      CYLINDER_LENGTH,
      24
    );
    const materialPool = COLORS.map(
      (color) =>
        new THREE.MeshPhongMaterial({
          color,
          shininess: 80,
          specular: new THREE.Color(0x9fb8ff),
          emissive: new THREE.Color(color === 0x151821 ? 0x0b0f18 : 0x05070b),
          emissiveIntensity: color === 0x151821 ? 0.55 : 0.14,
        })
    );
    const debugLinePositions = new Float32Array(DEBUG_LINE_COUNT * 2 * 3);
    const debugBondPositions = new Float32Array(DEBUG_LINE_COUNT * 2 * 3);

    const elements = [];
    const pairEngagement = new Map();
    const tempWorldPortA = new CANNON.Vec3();
    const tempWorldPortB = new CANNON.Vec3();
    const tempForce = new CANNON.Vec3();
    const orbitTangent = new CANNON.Vec3();
    const debugTempPosition = new THREE.Vector3();
    const frameStep = 1 / 60;
    const spawn = (colorIndex) => {
      const mesh = createConnectorMesh(sharedGeometry, materialPool[colorIndex]);
      const body = createBody();
      const orbitAxis = new CANNON.Vec3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5
      );

      if (orbitAxis.lengthSquared() < 0.001) {
        orbitAxis.set(0.3, 0.7, 0.2);
      }
      orbitAxis.normalize();

      scene.add(mesh);
      world.addBody(body);

      elements.push({
        mesh,
        body,
        colorIndex,
        orbitAxis,
      });
    };

    for (let i = 0; i < NUM_ELEMENTS; i += 1) {
      spawn(i % COLORS.length);
    }

    const syncMeshes = () => {
      elements.forEach(({ mesh, body }) => {
        mesh.position.copy(body.position);
        mesh.quaternion.copy(body.quaternion);
      });
    };

    const handleResize = () => {
      const { width, height } = mount.getBoundingClientRect();
      const safeWidth = Math.max(Math.round(width), 1);
      const safeHeight = Math.max(Math.round(height), 1);

      hasValidViewport = width >= 2 && height >= 2;

      camera.aspect = safeWidth / safeHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(safeWidth, safeHeight, false);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    for (let i = 0; i < 28; i += 1) {
      world.step(1 / 60, 1 / 60, 5);
    }
    syncMeshes();

    let rafId = 0;

    const animate = () => {
      rafId = window.requestAnimationFrame(animate);

      if (!hasValidViewport) {
        handleResize();
        if (!hasValidViewport) {
          return;
        }
      }

      world.step(1 / 60, 1 / 60, 5);

      let debugLineIndex = 0;
      let debugBondIndex = 0;
      const activeEngagementKeys = new Set();

      elements.forEach((elementA, indexA) => {
        const { body: bodyA, orbitAxis } = elementA;
        const positionA = bodyA.position;
        const toCenter = new CANNON.Vec3(
          -positionA.x,
          -positionA.y,
          -positionA.z
        );
        const distanceToCenter = toCenter.length();

        if (distanceToCenter > 0.0001) {
          toCenter.scale(1 / distanceToCenter, toCenter);
        }

        toCenter.cross(orbitAxis, orbitTangent);
        orbitTangent.scale(0.6, orbitTangent);
        toCenter.scale(distanceToCenter * 0.38, tempForce);
        orbitTangent.vadd(tempForce, tempForce);
        bodyA.applyForce(tempForce, positionA);
        bodyA.angularDamping = 0.94;
        bodyA.linearDamping = 0.72;

        for (let indexB = indexA + 1; indexB < elements.length; indexB += 1) {
          const elementB = elements[indexB];
          const bodyB = elementB.body;
          const canBond = elementA.colorIndex === elementB.colorIndex;
          let bestPortAIndex = -1;
          let bestPortBIndex = -1;
          let bestDistance = Infinity;
          let bestPortAX = 0;
          let bestPortAY = 0;
          let bestPortAZ = 0;
          let bestPortBX = 0;
          let bestPortBY = 0;
          let bestPortBZ = 0;

          for (let portAIndex = 0; portAIndex < LOCAL_PORTS.length; portAIndex += 1) {
            for (let portBIndex = 0; portBIndex < LOCAL_PORTS.length; portBIndex += 1) {
              bodyA.pointToWorldFrame(LOCAL_PORTS[portAIndex], tempWorldPortA);
              bodyB.pointToWorldFrame(LOCAL_PORTS[portBIndex], tempWorldPortB);

              const distance = tempWorldPortA.distanceTo(tempWorldPortB);
              if (distance >= MAGNET_DISTANCE || distance >= bestDistance) continue;

              bestDistance = distance;
              bestPortAIndex = portAIndex;
              bestPortBIndex = portBIndex;
              bestPortAX = tempWorldPortA.x;
              bestPortAY = tempWorldPortA.y;
              bestPortAZ = tempWorldPortA.z;
              bestPortBX = tempWorldPortB.x;
              bestPortBY = tempWorldPortB.y;
              bestPortBZ = tempWorldPortB.z;
            }
          }

          if (bestPortAIndex === -1 || bestPortBIndex === -1) {
            continue;
          }

          tempWorldPortA.set(bestPortAX, bestPortAY, bestPortAZ);
          tempWorldPortB.set(bestPortBX, bestPortBY, bestPortBZ);
          if (debugLineIndex < DEBUG_LINE_COUNT) {
            const lineOffset = debugLineIndex * 6;
            debugLinePositions[lineOffset] = bestPortAX;
            debugLinePositions[lineOffset + 1] = bestPortAY;
            debugLinePositions[lineOffset + 2] = bestPortAZ;
            debugLinePositions[lineOffset + 3] = bestPortBX;
            debugLinePositions[lineOffset + 4] = bestPortBY;
            debugLinePositions[lineOffset + 5] = bestPortBZ;
            debugLineIndex += 1;
          }

          const engagementKey = `${indexA}:${bestPortAIndex}-${indexB}:${bestPortBIndex}`;
          activeEngagementKeys.add(engagementKey);

          let strength = BASE_REPULSION_STRENGTH;
          if (canBond) {
            const nextEngagement = (pairEngagement.get(engagementKey) || 0) + frameStep;
            pairEngagement.set(engagementKey, nextEngagement);
            strength = Math.min(
              BASE_ATTRACTION_STRENGTH + nextEngagement * ATTRACTION_RAMP_PER_SECOND,
              MAX_ATTRACTION_STRENGTH
            );
          }

          tempWorldPortB.vsub(tempWorldPortA, tempForce);
          tempForce.scale(strength, tempForce);
          bodyA.applyForce(tempForce, tempWorldPortA);
          tempForce.scale(-1, tempForce);
          bodyB.applyForce(tempForce, tempWorldPortB);

          if (canBond && bestDistance < SNAP_DISTANCE && debugBondIndex < DEBUG_LINE_COUNT) {
            const bondOffset = debugBondIndex * 6;
            debugBondPositions[bondOffset] = bestPortAX;
            debugBondPositions[bondOffset + 1] = bestPortAY;
            debugBondPositions[bondOffset + 2] = bestPortAZ;
            debugBondPositions[bondOffset + 3] = bestPortBX;
            debugBondPositions[bondOffset + 4] = bestPortBY;
            debugBondPositions[bondOffset + 5] = bestPortBZ;
            debugBondIndex += 1;
          }
        }

        elementA.mesh.position.copy(bodyA.position);
        elementA.mesh.quaternion.copy(bodyA.quaternion);

        for (let portIndex = 0; portIndex < LOCAL_PORTS.length; portIndex += 1) {
          bodyA.pointToWorldFrame(LOCAL_PORTS[portIndex], tempWorldPortA);
          debugTempPosition.set(tempWorldPortA.x, tempWorldPortA.y, tempWorldPortA.z);
        }
      });

      for (const key of pairEngagement.keys()) {
        if (!activeEngagementKeys.has(key)) {
          pairEngagement.delete(key);
        }
      }

      controls.update();
      renderer.render(scene, camera);

      if (!isSceneVisible) {
        isSceneVisible = true;
        renderer.domElement.classList.add(styles.canvasReady);
      }
    };

    animate();

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      elements.forEach(({ mesh, body }) => {
        scene.remove(mesh);
        world.removeBody(body);
      });

      sharedGeometry.dispose();
      materialPool.forEach((material) => material.dispose());
      controls.dispose();
      renderer.dispose();

      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className={styles.visual}>
      <div className={styles.canvasMount} ref={mountRef} />
    </div>
  );
}

export default LayeredDataCore;
